import React from 'react';
import KeyValueGrid from '../../../components/KeyValueGrid';

// ==================== STYLES ====================
export const commonStyles = {
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  colors: {
    primary: '#0d6efd',
    success: '#198754',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#0dcaf0',
    light: '#f8f9fa',
    dark: '#212529',
    muted: '#6c757d',
    border: '#e9ecef',
    borderDark: '#dee2e6',
  },
  typography: {
    h1: { fontSize: '24px', fontWeight: '600' },
    h2: { fontSize: '20px', fontWeight: '600' },
    h3: { fontSize: '16px', fontWeight: '600' },
    h4: { fontSize: '14px', fontWeight: '600' },
    body: { fontSize: '14px' },
    small: { fontSize: '12px' },
    label: { fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  },
  shadows: {
    sm: '0 1px 3px rgba(0,0,0,0.05)',
    md: '0 1px 3px rgba(0,0,0,0.08)',
    lg: '0 4px 6px rgba(0,0,0,0.1)',
  },
  borders: {
    radius: '6px',
    radiusLg: '8px',
  },
};

// ==================== CARD COMPONENT ====================
export const Card = ({
  children,
  title,
  subtitle,
  icon,
  actions,
  className,
  style,
}) => {
  return (
    <div
      className={className}
      style={{
        backgroundColor: '#ffffff',
        border: `1px solid ${commonStyles.colors.border}`,
        borderRadius: commonStyles.borders.radius,
        padding: commonStyles.spacing.md,
        marginBottom: commonStyles.spacing.md,
        boxShadow: commonStyles.shadows.sm,
        ...style,
      }}
    >
      {(title || subtitle || icon) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: commonStyles.spacing.md,
            paddingBottom: commonStyles.spacing.sm,
            borderBottom: `1px solid ${commonStyles.colors.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: commonStyles.spacing.sm }}>
            {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
            <div>
              {title && (
                <div style={{ ...commonStyles.typography.h4, color: commonStyles.colors.dark }}>
                  {title}
                </div>
              )}
              {subtitle && (
                <div
                  style={{
                    ...commonStyles.typography.small,
                    color: commonStyles.colors.muted,
                    marginTop: '0.25rem',
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
          </div>
          {actions && <div style={{ display: 'flex', gap: commonStyles.spacing.sm }}>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

// ==================== DATA TABLE COMPONENT ====================
export const DataTable = ({
  columns,
  data,
  loading,
  empty,
  onRowClick,
  striped = true,
  hover = true,
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: commonStyles.spacing.xl }}>
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ color: commonStyles.colors.muted }}>Loading data...</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: commonStyles.spacing.xl }}>
        <p style={{ color: commonStyles.colors.muted }}>{empty || 'No data available'}</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: 0,
          fontSize: commonStyles.typography.small.fontSize,
        }}
      >
        <thead>
          <tr style={{ backgroundColor: commonStyles.colors.light }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  ...commonStyles.typography.label,
                  color: commonStyles.colors.dark,
                  padding: commonStyles.spacing.sm,
                  textAlign: col.align || 'left',
                  borderBottom: `2px solid ${commonStyles.colors.borderDark}`,
                  width: col.width,
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              style={{
                backgroundColor: striped && rowIndex % 2 === 1 ? commonStyles.colors.light : '#ffffff',
                borderBottomColor: commonStyles.colors.border,
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: hover ? 'background-color 0.2s ease' : 'none',
              }}
              onMouseEnter={(e) => {
                if (hover) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (hover) {
                  e.currentTarget.style.backgroundColor =
                    striped && rowIndex % 2 === 1 ? commonStyles.colors.light : '#ffffff';
                }
              }}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: commonStyles.spacing.sm,
                    textAlign: col.align || 'left',
                    color: commonStyles.colors.dark,
                  }}
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== STAT CARD COMPONENT ====================
export const StatCard = ({
  icon,
  label,
  value,
  subtext,
  trend,
  trendValue,
  color = 'primary',
}) => {
  const colorMap = {
    primary: commonStyles.colors.primary,
    success: commonStyles.colors.success,
    danger: commonStyles.colors.danger,
    warning: commonStyles.colors.warning,
    info: commonStyles.colors.info,
  };

  return (
    <Card
      style={{
        textAlign: 'center',
        paddingBottom: commonStyles.spacing.lg,
      }}
    >
      <div style={{ fontSize: '28px', marginBottom: commonStyles.spacing.sm }}>{icon}</div>
      <div
        style={{
          ...commonStyles.typography.small,
          color: commonStyles.colors.muted,
          marginBottom: commonStyles.spacing.xs,
        }}
      >
        {label}
      </div>
      <div
        style={{
          ...commonStyles.typography.h2,
          color: colorMap[color],
          marginBottom: commonStyles.spacing.sm,
        }}
      >
        {value}
      </div>
      {(subtext || trend) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: commonStyles.spacing.xs,
            ...commonStyles.typography.small,
            color: commonStyles.colors.muted,
          }}
        >
          {subtext && <span>{subtext}</span>}
          {trend && (
            <span
              style={{
                color: trend === 'up' ? commonStyles.colors.success : commonStyles.colors.danger,
              }}
            >
              {trend === 'up' ? '▲' : '▼'} {trendValue}
            </span>
          )}
        </div>
      )}
    </Card>
  );
};

// ==================== BADGE COMPONENT ====================
export const Badge = ({ label, variant = 'primary', className, style }) => {
  const variants = {
    primary: { backgroundColor: commonStyles.colors.primary, color: '#ffffff' },
    success: { backgroundColor: commonStyles.colors.success, color: '#ffffff' },
    danger: { backgroundColor: commonStyles.colors.danger, color: '#ffffff' },
    warning: { backgroundColor: commonStyles.colors.warning, color: '#000000' },
    info: { backgroundColor: commonStyles.colors.info, color: '#ffffff' },
    light: { backgroundColor: commonStyles.colors.light, color: commonStyles.colors.dark },
  };

  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        padding: `${commonStyles.spacing.xs} ${commonStyles.spacing.sm}`,
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        ...variants[variant],
        ...style,
      }}
    >
      {label}
    </span>
  );
};

// ==================== EMPTY STATE COMPONENT ====================
export const EmptyState = ({ icon, title, description, action }) => {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: commonStyles.spacing.xl,
        color: commonStyles.colors.muted,
      }}
    >
      <div style={{ fontSize: '48px', marginBottom: commonStyles.spacing.md }}>{icon}</div>
      <div style={{ ...commonStyles.typography.h4, marginBottom: commonStyles.spacing.xs }}>
        {title}
      </div>
      {description && (
        <p style={{ marginBottom: commonStyles.spacing.md, fontSize: '13px' }}>
          {description}
        </p>
      )}
      {action && <div>{action}</div>}
    </div>
  );
};

// ==================== LOADING STATE COMPONENT ====================
export const LoadingState = ({ message = 'Loading...' }) => {
  return (
    <div style={{ textAlign: 'center', padding: commonStyles.spacing.xl }}>
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p style={{ color: commonStyles.colors.muted }}>{message}</p>
    </div>
  );
};

// ==================== STATS GRID COMPONENT ====================
export const StatsGrid = ({ stats, columns = 4 }) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${100 / columns}%, 1fr))`,
        gap: commonStyles.spacing.md,
        marginBottom: commonStyles.spacing.lg,
      }}
    >
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

// ==================== SECTION HEADER COMPONENT ====================
export const SectionHeader = ({ icon, title, subtitle, action }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: commonStyles.spacing.md,
        paddingBottom: commonStyles.spacing.sm,
        borderBottom: `1px solid ${commonStyles.colors.border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: commonStyles.spacing.sm }}>
        {icon && <span style={{ fontSize: '20px' }}>{icon}</span>}
        <div>
          <div style={{ ...commonStyles.typography.h3, color: commonStyles.colors.dark }}>
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                ...commonStyles.typography.small,
                color: commonStyles.colors.muted,
                marginTop: '0.25rem',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

// ==================== KEY VALUE GRID COMPONENT ====================
export { KeyValueGrid };